/**
 * Document configuration
 * Maps document enums to their URLs and metadata
 */

export enum DocumentType {
  TERMS_AND_CONDITIONS = 'TERMS_AND_CONDITIONS',
  PRIVACY_POLICY = 'PRIVACY_POLICY',
  DISCLAIMER = 'DISCLAIMER'
}

export interface DocumentConfig {
  url: string
  title: string
  name: string
}

export const DOCUMENT_CONFIG: Record<DocumentType, DocumentConfig> = {
  [DocumentType.TERMS_AND_CONDITIONS]: {
    url: 'https://cdn.powerup.money/documents/website/uni-wealth-tandc/uni-wealth-tandc_v3.docx',
    title: 'Terms and Conditions',
    name: 'Terms and Conditions'
  },
  [DocumentType.PRIVACY_POLICY]: {
    url: 'https://example.com/documents/privacy-policy.pdf',
    title: 'Privacy Policy',
    name: 'Privacy Policy'
  },
  [DocumentType.DISCLAIMER]: {
    url: 'https://example.com/documents/disclaimer.pdf',
    title: 'Disclaimer',
    name: 'Disclaimer'
  }
}

/**
 * Get document configuration for a given enum
 * Returns the default TERMS_AND_CONDITIONS if enum is not found
 */
export function getDocumentConfig(documentType: string): DocumentConfig {
  const type = documentType as DocumentType
  if (type && DOCUMENT_CONFIG[type]) {
    return DOCUMENT_CONFIG[type]
  }
  // Return default document config
  return DOCUMENT_CONFIG[DocumentType.TERMS_AND_CONDITIONS]
}

/**
 * Get document URL from enum or return the URL directly if it's already a URL
 */
export function getDocumentUrl(docParam: string | null): string {
  if (!docParam) {
    return DOCUMENT_CONFIG[DocumentType.TERMS_AND_CONDITIONS].url
  }

  // Check if it's a valid URL (starts with http:// or https://)
  if (docParam.startsWith('http://') || docParam.startsWith('https://')) {
    return docParam
  }

  // Otherwise, treat it as an enum and get the config
  const config = getDocumentConfig(docParam)
  return config.url
}

/**
 * Get document metadata (title and name) from enum or use defaults
 */
export function getDocumentMetadata(docParam: string | null): { title: string; name: string } {
  if (!docParam) {
    const defaultConfig = DOCUMENT_CONFIG[DocumentType.TERMS_AND_CONDITIONS]
    return { title: defaultConfig.title, name: defaultConfig.name }
  }

  // Check if it's a URL
  if (docParam.startsWith('http://') || docParam.startsWith('https://')) {
    // Extract filename from URL or use a default name
    const urlParts = docParam.split('/')
    const filename = urlParts[urlParts.length - 1] || 'Document'
    return { title: filename, name: filename }
  }

  // Otherwise, treat it as an enum and get the config
  const config = getDocumentConfig(docParam)
  return { title: config.title, name: config.name }
}
